import { Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import dayjs from 'dayjs';

import { RHFTextField, RHFDatePicker, Iconify } from '@app/components';
import { Strings } from '@app/constants';
import { StyledInputs } from '@app/modules/assets/style';
import { IWorkExperience } from '@app/modules/my-profile/types';

const initialWorkExperience: IWorkExperience = {
  companyName: '',
  designationJobTitle: '',
  fromDate: '',
  toDate: '',
  jobDescription: '',
};

interface props {
  onClose?: () => void;
  additionalButton?: React.ReactNode;
  workExperiences?: IWorkExperience[];
  updateWorkExperienceState?: (updatedWorkExperience?: IWorkExperience[]) => void;
  labelSuffix?: boolean;
}

const WorkExperienceInfoForm = ({
  additionalButton,
  updateWorkExperienceState,
  labelSuffix,
  workExperiences,
}: props) => {
  const [workExperience, setWorkExperience] = useState(
    workExperiences ? workExperiences ?? [] : [initialWorkExperience]
  );

  const renderInputFieldSets = () => {
    return workExperience?.map((workExperience: any, index: number) => (
      <>
        {index === 0 ? null : (
          <Stack alignItems={'flex-end'} pt={1}>
            <IconButton
              onClick={() => {
                handleRemoveClick(index);
              }}
              sx={{
                width: '3vw',
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Iconify icon="material-symbols:close" />
            </IconButton>
          </Stack>
        )}
        <Typography variant="h5" pt={2}>
          {Strings.workExperience.workExperience}
        </Typography>
        <Stack gap={2} flexWrap={'wrap'} pt={1}>
          <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
            <RHFTextField
              name={`workExperienceInfo.workExperience[${index}].companyName`}
              label={Strings.workExperience.companyName}
              fullWidth
            />
            <RHFTextField
              name={`workExperienceInfo.workExperience[${index}].designationJobTitle`}
              label={Strings.workExperience.designationJobTitle}
              fullWidth
            />
          </Stack>
          <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
            <RHFDatePicker
              name={`workExperienceInfo.workExperience[${index}].fromDate`}
              label={Strings.workExperience.fromDate}
              sx={{ width: '100%' }}
              defaultValue={
                workExperience.fromDate ? dayjs(workExperience.fromDate, 'DD/MM/YYYY') : null
              }
            />
            <RHFDatePicker
              name={`workExperienceInfo.workExperience[${index}].toDate`}
              label={Strings.workExperience.toDate}
              sx={{ width: '100%' }}
              defaultValue={
                workExperience.toDate ? dayjs(workExperience.toDate, 'DD/MM/YYYY') : null
              }
            />
          </Stack>
          <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
            <Stack flex={1}>
              <RHFTextField
                name={`workExperienceInfo.workExperience[${index}].jobDescription`}
                label={Strings.workExperience.jobDescription}
                multiline
                minRows={3}
              />
            </Stack>
          </Stack>
        </Stack>
      </>
    ));
  };

  const handleAddMoreClick = () => {
    setWorkExperience((prevWorkExperience: any) => [
      ...prevWorkExperience,
      { ...initialWorkExperience },
    ]);
  };

  const handleRemoveClick = (index: number) => {
    const updatedWorkExperience = workExperience?.filter((_, i) => i !== index);
    setWorkExperience(updatedWorkExperience);
    updateWorkExperienceState?.(updatedWorkExperience);
  };

  return (
    <Stack>
      <Divider />
      <StyledInputs>
        {renderInputFieldSets()}
        <Stack flexDirection={'row'} justifyContent={'space-between'} mt={2}>
          <Stack>{additionalButton}</Stack>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ width: 'max-content' }}
            onClick={handleAddMoreClick}
          >
            {Strings.button.addMore}
            <IconButton>
              <Iconify icon="ic:outline-plus" />
            </IconButton>
          </Button>
        </Stack>
      </StyledInputs>
    </Stack>
  );
};
export { WorkExperienceInfoForm };
